import { ManagedVolume, PLATFORM_VOLUME_KEY } from '../StorageAPI';

/**
 * The two rules the Firmenprofil volume settings apply to `defaultManagedFileVolume`.
 *
 * Both exist because the stored configuration is not the whole truth about a customer's volumes:
 * a customer still carrying the pre-volumes `managedFileVolume` block has that lifted into a real
 * volume by `GET /volumes`, and it appears nowhere in `configuration.managedFileVolumes`.
 */

/**
 * Which volume the default-volume dropdown should show as selected.
 *
 * The server's own answer wins, because it is the one that decides where generated files actually
 * land — for an unmigrated SharePoint customer that is their SharePoint volume, while the stored
 * configuration says nothing at all. The stored key is only a fallback for the moment before the
 * volume list has loaded.
 */
export const resolveDefaultVolumeKey = (
    serverVolumes: ManagedVolume[],
    storedKey?: string
): string =>
    serverVolumes.find(volume => volume.isDefault)?.key ?? storedKey ?? PLATFORM_VOLUME_KEY;

/**
 * What to write back as `defaultManagedFileVolume`; `undefined` means the field is removed.
 *
 * `requested === undefined` means "this save is not about the default" and must carry the stored
 * value through **unchanged, including unset**. Writing the resolved key back instead materialised
 * an explicit `managed-documents` for customers who had never chosen one, which silently repointed
 * where their generated files land: an unmigrated SharePoint customer resolves to their SharePoint
 * site, so saving anything in this panel moved them to an empty S3 volume.
 *
 * `null` clears it — used when the volume being removed is the one the default pointed at, where
 * leaving it would dangle and naming the platform volume would move the files of a customer whose
 * SharePoint volume is only lifted.
 */
export const nextDefaultVolumeKey = (
    requested: string | null | undefined,
    storedKey: string | undefined
): string | undefined => (requested === undefined ? storedKey : requested || undefined);

/**
 * Whether a key is already taken by one of the customer's volumes.
 *
 * Judged against what `GET /volumes` reports, not only the declared `managedFileVolumes[]`. A
 * lifted SharePoint volume appears in no declared entry, so checking the configuration alone let
 * an admin create an additional volume under the lifted volume's key — and the lift stands down as
 * soon as a declared entry claims that key, so the tenant's existing SharePoint files would
 * silently start resolving against whichever site the new volume points at.
 *
 * Declared keys are unioned in rather than assumed to be a subset: the two lists are produced by
 * different code paths, and a key missing from either is the failure this guards.
 */
/**
 * The keys of volumes restricted to roles, read off the stored `managedFileVolumes[]`.
 *
 * Read from the configuration rather than from `GET /volumes`, which does not carry `roles`: the
 * volume list is filtered for the *caller*, and a customerAdministrator — the only person who can
 * open this panel — sees every volume regardless of its restriction, so the server's list cannot
 * answer "is this one restricted".
 *
 * A restriction of nothing but blanks is not one: the server normalizes those away and treats the
 * volume as available to everyone, so this must agree or the panel would hide a default-volume
 * option the server would happily accept.
 */
export const restrictedVolumeKeys = (
    declaredVolumes: Array<{ key?: unknown; roles?: unknown }> = []
): Set<string> =>
    new Set(
        declaredVolumes
            .filter(volume =>
                Array.isArray(volume?.roles) &&
                volume.roles.some((role: unknown) => String(role ?? '').trim())
            )
            .map(volume => String(volume?.key ?? ''))
    );

export const isVolumeKeyTaken = (
    key: string,
    serverVolumes: ManagedVolume[],
    declaredVolumes: Array<{ key?: unknown }> = []
): boolean => {
    const taken = new Set<string>([
        ...serverVolumes.map(volume => volume.key),
        ...declaredVolumes.map(volume => String(volume?.key ?? '')),
    ]);
    return taken.has(key);
};
